<template>
    <div>
        <!-- 遮罩层 -->
        <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 z-40" @click="close"></div>

        <!-- 抽屉 -->
        <div
            class="fixed top-0 right-0 h-full bg-base-100 shadow-xl z-50 transition-transform duration-300 ease-in-out"
            :class="isOpen ? 'translate-x-0' : 'translate-x-full'"
            style="width: 600px;"
        >
            <div class="flex flex-col h-full p-6">
                <div class="flex items-center justify-between mb-6">
                    <h3 class="text-lg font-bold">{{ $tt('分享播放列表') }}</h3>
                    <button class="btn btn-sm btn-circle btn-ghost" @click="close">✕</button>
                </div>

            <!-- Tab 切换 -->
            <div role="tablist" class="tabs tabs-boxed mb-4">
                <a role="tab" class="tab" :class="{'tab-active': activeTab === 'create'}" @click="activeTab = 'create'">
                    {{ $tt('创建分享') }}
                </a>
                <a role="tab" class="tab" :class="{'tab-active': activeTab === 'manage'}" @click="activeTab = 'manage'">
                    {{ $tt('分享管理') }}
                </a>
            </div>

            <!-- 内容区域 - 可滚动 -->
            <div class="flex-1 overflow-y-auto">
                <!-- 创建分享 Tab -->
                <div v-if="activeTab === 'create'" class="space-y-4">
                        <!-- 分享方式 -->
                        <div class="form-control">
                            <label class="label">
                                <span class="label-text">{{ $tt('分享方式') }}</span>
                            </label>
                            <select v-model="shareForm.share_type" class="select select-bordered w-full">
                                <!-- <option value="link">{{ $tt('公开链接') }}</option> -->
                                <option value="user">{{ $tt('指定用户') }}</option>
                            </select>
                        </div>

                        <!-- 指定用户选择器（仅在share_type为user时显示） -->
                        <div v-if="shareForm.share_type === 'user'" class="form-control">
                            <label class="label">
                                <span class="label-text">{{ $tt('选择用户') }}</span>
                            </label>
                            <div class="space-y-2">
                                <!-- 用户搜索框 -->
                                <div class="relative" ref="userSearchContainer">
                                    <input
                                        type="text"
                                        v-model="userSearchQuery"
                                        @input="searchUsers"
                                        @focus="onSearchFocus"
                                        :placeholder="$tt('搜索用户名...')"
                                        class="input input-bordered w-full"
                                    />
                                    <!-- 搜索结果下拉列表 -->
                                    <div v-if="showUserDropdown && filteredUsers.length > 0"
                                         class="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                        <div
                                            v-for="user in filteredUsers"
                                            :key="user.id"
                                            @mousedown.prevent="addUser(user)"
                                            class="px-4 py-2 hover:bg-base-200 cursor-pointer flex items-center justify-between"
                                        >
                                            <div>
                                                <div class="font-medium">{{ user.username }}</div>
                                                <div v-if="user.email" class="text-sm text-gray-500">{{ user.email }}</div>
                                            </div>
                                            <button class="btn btn-xs btn-neutral">{{ $tt('添加') }}</button>
                                        </div>
                                    </div>
                                    <!-- 无搜索结果提示 -->
                                    <div v-if="showUserDropdown && userSearchQuery && filteredUsers.length === 0"
                                         class="absolute z-10 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg px-4 py-3 text-center text-gray-500">
                                        {{ $tt('未找到匹配的用户') }}
                                    </div>
                                </div>

                                <!-- 已选择的用户列表 -->
                                <div v-if="shareForm.selected_users.length > 0" class="space-y-2">
                                    <div class="text-sm text-gray-500">{{ $tt('已选择 {count} 个用户：', { count: shareForm.selected_users.length }) }}</div>
                                    <div class="flex flex-wrap gap-2">
                                        <div
                                            v-for="user in shareForm.selected_users"
                                            :key="user.id"
                                            class="badge badge-lg gap-2"
                                        >
                                            {{ user.username }}
                                            <button @click="removeUser(user.id)" class="btn btn-xs btn-circle btn-ghost">✕</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 权限级别 -->
                        <div class="form-control">
                            <label class="label">
                                <span class="label-text">{{ $tt('权限级别') }}</span>
                            </label>
                            <select
                                v-model="shareForm.permission"
                                class="select select-bordered w-full"
                                @change="onPermissionChange"
                                key="permission-select"
                            >
                                <option value="view">{{ $tt('仅查看') }}</option>
                                <option value="copy">{{ $tt('可复制') }}</option>
                                <option value="edit">{{ $tt('可编辑') }}</option>
                            </select>
                            <div class="text-xs text-gray-500 mt-1">
                                {{ $tt('当前选择: {value} (类型: {type})', { value: shareForm.permission, type: typeof shareForm.permission }) }}
                            </div>
                        </div>

                        <!-- 有效期（仅在link模式显示） -->
                        <div v-if="shareForm.share_type === 'link'" class="form-control">
                            <label class="label">
                                <span class="label-text">{{ $tt('有效期') }}</span>
                            </label>
                            <select
                                v-model="shareForm.expires_in_days"
                                class="select select-bordered w-full"
                                key="expires-select"
                            >
                                <option :value="null">{{ $tt('永久有效') }}</option>
                                <option :value="7">{{ $tt('7天') }}</option>
                                <option :value="30">{{ $tt('30天') }}</option>
                                <option :value="90">{{ $tt('90天') }}</option>
                            </select>
                            <div class="text-xs text-gray-500 mt-1">
                                {{ $tt('当前选择: {value} (类型: {type})', { value: shareForm.expires_in_days, type: typeof shareForm.expires_in_days }) }}
                            </div>
                        </div>

                        <!-- 访问密码（仅在link模式显示） -->
                        <div v-if="shareForm.share_type === 'link'" class="form-control">
                            <label class="label">
                                <span class="label-text">{{ $tt('访问密码（可选）') }}</span>
                            </label>
                            <input
                                type="text"
                                v-model="shareForm.access_password"
                                :placeholder="$tt('留空表示不设置密码')"
                                class="input input-bordered w-full"
                            />
                        </div>

                        <!-- 生成分享按钮 -->
                        <button
                            class="btn btn-neutral btn-block"
                            @click="createShare"
                            :disabled="isCreating || (shareForm.share_type === 'user' && shareForm.selected_users.length === 0)"
                        >
                            <span v-if="isCreating" class="loading loading-spinner"></span>
                            {{ isCreating ? $tt('生成中...') : (shareForm.share_type === 'link' ? $tt('生成分享链接') : $tt('分享给选中用户')) }}
                        </button>

                        <!-- 显示生成的分享链接 -->
                        <div v-if="generatedShareUrl" class="alert alert-success">
                            <div class="flex flex-col w-full">
                                <span class="font-bold mb-2">{{ $tt('分享链接已生成：') }}</span>
                                <div class="flex items-center gap-2">
                                    <input
                                        type="text"
                                        :value="generatedShareUrl"
                                        readonly
                                        class="input input-bordered input-sm flex-1"
                                    />
                                    <button class="btn btn-sm btn-neutral" @click="copyShareUrl">
                                        {{ $tt('复制') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 分享管理 Tab -->
                    <div v-if="activeTab === 'manage'">
                        <div v-if="isLoadingShares" class="flex justify-center py-8">
                            <span class="loading loading-spinner loading-lg"></span>
                        </div>

                        <div v-else-if="shares.length === 0" class="text-center py-8 text-gray-500">
                            {{ $tt('暂无分享记录') }}
                        </div>

                        <div v-else class="overflow-x-auto">
                            <table class="table table-zebra table-sm">
                                <thead>
                                    <tr>
                                        <th>{{ $tt('类型') }}</th>
                                        <th>{{ $tt('分享对象') }}</th>
                                        <th>{{ $tt('权限') }}</th>
                                        <th>{{ $tt('访问次数') }}</th>
                                        <th>{{ $tt('创建时间') }}</th>
                                        <th>{{ $tt('过期时间') }}</th>
                                        <th>{{ $tt('状态') }}</th>
                                        <th>{{ $tt('操作') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="share in shares" :key="share.id">
                                        <td>
                                            <span class="badge badge-sm" :class="share.share_type === 'link' ? 'badge-info' : 'badge-primary'">
                                                {{ share.share_type_display }}
                                            </span>
                                        </td>
                                        <td>
                                            <span v-if="share.share_type === 'link'">{{ $tt('公开链接') }}</span>
                                            <span v-else>{{ share.shared_with_username || $tt('未知用户') }}</span>
                                        </td>
                                        <td>{{ share.permission_display }}</td>
                                        <td>{{ share.access_count }}</td>
                                        <td>{{ formatDate(share.created_at) }}</td>
                                        <td>{{ share.expires_at ? formatDate(share.expires_at) : $tt('永久') }}</td>
                                        <td>
                                            <span
                                                class="badge badge-sm"
                                                :class="share.is_active ? 'badge-success' : 'badge-error'"
                                            >
                                                {{ share.is_active ? $tt('有效') : $tt('已撤销') }}
                                            </span>
                                        </td>
                                        <td>
                                            <div class="flex gap-2">
                                                <button
                                                    v-if="share.is_active && share.share_type === 'link'"
                                                    class="btn btn-xs btn-neutral"
                                                    @click="copyUrl(share.share_url)"
                                                >
                                                    {{ $tt('复制链接') }}
                                                </button>
                                                <button
                                                    v-if="share.is_active"
                                                    class="btn btn-xs btn-error"
                                                    @click="revokeShare(share.id)"
                                                >
                                                    {{ $tt('撤销') }}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PlaylistShareModal',
    props: {
        playlistId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            isOpen: false,
            activeTab: 'create',
            shareForm: {
                share_type: 'user',
                permission: 'view',
                expires_in_days: null,
                access_password: '',
                selected_users: []
            },
            isCreating: false,
            generatedShareUrl: '',
            shares: [],
            isLoadingShares: false,
            // 用户搜索相关
            userSearchQuery: '',
            allUsers: [],
            filteredUsers: [],
            showUserDropdown: false,
            isLoadingUsers: false
        }
    },
    watch: {
        'shareForm.share_type': {
            handler(newVal, oldVal) {
                console.log('shareForm.share_type 变化:', oldVal, '->', newVal)
            }
        },
        'shareForm.expires_in_days': {
            handler(newVal, oldVal) {
                console.log('shareForm.expires_in_days 变化:', oldVal, '->', newVal)
            }
        },
        'shareForm.permission': {
            handler(newVal, oldVal) {
                console.log('shareForm.permission 变化:', oldVal, '->', newVal)
                // 如果变成了 null 或 undefined，重置为 'view'
                if (!newVal) {
                    console.warn('检测到 permission 为空，重置为 view')
                    this.$nextTick(() => {
                        this.shareForm.permission = 'view'
                    })
                }
                // 如果是数字类型，说明被错误赋值了
                if (typeof newVal === 'number') {
                    console.error('检测到 permission 是数字类型，重置为 view')
                    this.$nextTick(() => {
                        this.shareForm.permission = 'view'
                    })
                }
            },
            immediate: true
        }
    },
    mounted() {
        // 点击外部关闭下拉列表
        document.addEventListener('click', this.handleClickOutside)
    },
    created() {
        // 确保 permission 有初始值
        console.log('组件创建时 shareForm.permission:', this.shareForm.permission)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
    },
    methods: {
        normalizeUser(user) {
            if (!user || user.id === undefined || user.id === null) {
                return null
            }

            const username = user.username || user.name || user.nickname || ''
            return {
                id: user.id,
                username,
                email: user.email || ''
            }
        },

        getCurrentUserId() {
            return this.$store?.state?.user?.id ?? this.$store?.state?.common?.userId ?? null
        },

        getCurrentUsername() {
            return (this.$store?.state?.common?.username || '').toLowerCase()
        },

        isCurrentUser(user) {
            if (!user) {
                return false
            }

            const currentUserId = this.getCurrentUserId()
            if (currentUserId !== null && String(user.id) === String(currentUserId)) {
                return true
            }

            const currentUsername = this.getCurrentUsername()
            const username = (user.username || '').toLowerCase()
            return Boolean(currentUsername && username && currentUsername === username)
        },

        getAvailableUsers(query = '') {
            const normalizedQuery = query.trim().toLowerCase()

            return this.allUsers.filter(user => {
                if (!user || user.id === undefined || user.id === null) {
                    return false
                }

                if (this.isCurrentUser(user)) {
                    return false
                }

                if (this.shareForm.selected_users.some(selectedUser => String(selectedUser.id) === String(user.id))) {
                    return false
                }

                if (!normalizedQuery) {
                    return true
                }

                const username = (user.username || '').toLowerCase()
                const email = (user.email || '').toLowerCase()
                return username.includes(normalizedQuery) || email.includes(normalizedQuery)
            }).slice(0, 10)
        },

        onPermissionChange() {
            console.log('权限级别改变:', this.shareForm.permission)
        },

        show() {
            this.isOpen = true
            this.loadShares()
            this.loadUsers()
        },

        close() {
            this.isOpen = false
            this.generatedShareUrl = ''
            // 重置表单到初始状态 - 使用 Object.assign 保持响应式
            Object.assign(this.shareForm, {
                share_type: 'user',
                permission: 'view',
                expires_in_days: null,
                access_password: '',
                selected_users: []
            })
            this.userSearchQuery = ''
            this.showUserDropdown = false
        },

        async loadUsers() {
            if (this.allUsers.length > 0) return // 已加载过

            this.isLoadingUsers = true
            try {
                const res = await this.$api.user.listUsers()
                if (res.result) {
                    // 合并superusers和employees
                    const superusers = res.data.superusers || []
                    const employees = res.data.employees || []
                    this.allUsers = [...superusers, ...employees]
                        .map(this.normalizeUser)
                        .filter(Boolean)
                }
            } catch (error) {
                console.error('加载用户列表失败:', error)
            } finally {
                this.isLoadingUsers = false
            }
        },

        onSearchFocus() {
            // 当输入框获得焦点时，如果有搜索内容则显示下拉框
            if (this.userSearchQuery.trim()) {
                this.searchUsers()
            } else if (this.allUsers.length > 0) {
                this.filteredUsers = this.getAvailableUsers()
                this.showUserDropdown = this.filteredUsers.length > 0
            }
        },

        searchUsers() {
            if (!this.userSearchQuery.trim()) {
                this.filteredUsers = this.getAvailableUsers()
                this.showUserDropdown = this.filteredUsers.length > 0
                return
            }

            this.filteredUsers = this.getAvailableUsers(this.userSearchQuery)
            this.showUserDropdown = true
        },

        addUser(user) {
            const normalizedUser = this.normalizeUser(user)
            if (!normalizedUser) {
                return
            }

            if (this.isCurrentUser(normalizedUser)) {
                return
            }

            if (!this.shareForm.selected_users.some(selectedUser => String(selectedUser.id) === String(normalizedUser.id))) {
                this.shareForm.selected_users.push(normalizedUser)
            }
            this.userSearchQuery = ''
            this.showUserDropdown = false
            this.filteredUsers = this.getAvailableUsers()
        },

        removeUser(userId) {
            this.shareForm.selected_users = this.shareForm.selected_users.filter(u => u.id !== userId)
        },

        handleClickOutside(event) {
            // 检查点击是否在用户搜索框或下拉列表外部
            if (this.$refs.userSearchContainer && !this.$refs.userSearchContainer.contains(event.target)) {
                this.showUserDropdown = false
            }
        },

        async createShare() {
            this.isCreating = true
            try {
                const params = {
                    id: this.playlistId,
                    share_type: this.shareForm.share_type,
                    permission: this.shareForm.permission
                }

                // 调试日志
                console.log('创建分享 - 准备发送的参数:', params)
                console.log('shareForm.permission 值:', this.shareForm.permission, '类型:', typeof this.shareForm.permission)

                if (this.shareForm.share_type === 'link') {
                    params.expires_in_days = this.shareForm.expires_in_days
                    params.access_password = this.shareForm.access_password || undefined
                } else if (this.shareForm.share_type === 'user') {
                    params.shared_with_user_ids = this.shareForm.selected_users.map(u => u.id)
                }

                console.log('最终发送的参数:', params)

                const res = await this.$api.music.share.createShare(params)

                if (res.result) {
                    if (this.shareForm.share_type === 'link') {
                        this.generatedShareUrl = res.data.share_url
                        this.$myMsg.notify({
                            title: this.$tt('分享链接创建成功！'),
                            type: 'success',
                            time: 2000
                        })
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('成功分享给 {count} 个用户！', {
                                count: this.shareForm.selected_users.length
                            }),
                            type: 'success',
                            time: 2000
                        })
                        this.shareForm.selected_users = []
                    }
                    // 刷新分享列表
                    this.loadShares()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('创建失败'),
                        content: res.message || this.$tt('未知错误'),
                        type: 'error',
                        time: 2000
                    })
                }
            } catch (error) {
                console.error('创建分享失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('创建失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000
                })
            } finally {
                this.isCreating = false
            }
        },

        async loadShares() {
            this.isLoadingShares = true
            try {
                const res = await this.$api.music.share.listShares({ id: this.playlistId })
                if (res.result) {
                    this.shares = res.data
                }
            } catch (error) {
                console.error('加载分享列表失败:', error)
            } finally {
                this.isLoadingShares = false
            }
        },

        async revokeShare(shareId) {
            if (!confirm(this.$tt('确定要撤销此分享吗？撤销后分享链接将失效。'))) {
                return
            }

            try {
                const res = await this.$api.music.share.deleteShare({ share_id: shareId })
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('撤销成功！'),
                        type: 'success',
                        time: 2000
                    })
                    this.loadShares()
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('撤销失败'),
                        content: res.message || this.$tt('未知错误'),
                        type: 'error',
                        time: 2000
                    })
                }
            } catch (error) {
                console.error('撤销分享失败:', error)
                this.$myMsg.notify({
                    title: this.$tt('撤销失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000
                })
            }
        },

        copyShareUrl() {
            this.copyUrl(this.generatedShareUrl)
        },

        copyUrl(url) {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(url).then(() => {
                    this.$myMsg.notify({
                        title: this.$tt('已复制到剪贴板！'),
                        type: 'success',
                        time: 2000
                    })
                }).catch(() => {
                    this.fallbackCopy(url)
                })
            } else {
                this.fallbackCopy(url)
            }
        },

        fallbackCopy(text) {
            const textArea = document.createElement('textarea')
            textArea.value = text
            textArea.style.position = 'fixed'
            textArea.style.left = '-999999px'
            document.body.appendChild(textArea)
            textArea.select()
            try {
                document.execCommand('copy')
                this.$myMsg.notify({
                    title: this.$tt('已复制到剪贴板！'),
                    type: 'success',
                    time: 2000
                })
            } catch (err) {
                this.$myMsg.notify({
                    title: this.$tt('复制失败'),
                    content: this.$tt('请手动复制'),
                    type: 'error',
                    time: 2000
                })
            }
            document.body.removeChild(textArea)
        },

        formatDate(dateString) {
            if (!dateString) return ''
            const date = new Date(dateString)
            const now = new Date()
            const diff = now - date
            const days = Math.floor(diff / (1000 * 60 * 60 * 24))

            if (days === 0) {
                const hours = Math.floor(diff / (1000 * 60 * 60))
                if (hours === 0) {
                    const minutes = Math.floor(diff / (1000 * 60))
                    return this.$tt('{count}分钟前', { count: minutes })
                }
                return this.$tt('{count}小时前', { count: hours })
            } else if (days < 7) {
                return this.$tt('{count}天前', { count: days })
            } else {
                return date.toLocaleDateString()
            }
        }
    }
}
</script>

<style scoped>
/* 确保抽屉在最上层 */
.fixed {
    position: fixed;
}

/* 平滑过渡动画 */
.transition-transform {
    transition: transform 0.3s ease-in-out;
}

/* 内容区域滚动 */
.overflow-y-auto {
    overflow-y: auto;
}

/* 防止body滚动 */
body.drawer-open {
    overflow: hidden;
}
</style>
